/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useEffect, useRef } from 'react';
import { onboardingFormContext, onboardingInitialValues, type OnboardingValues } from '../context/OnboardingContext';

const STORAGE_KEY = 'onboarding:formValues';
const EPHEMERAL_KEYS: ReadonlySet<keyof OnboardingValues> = new Set([
  'paymentUrl',
]);

/**
 * Reads persisted onboarding form values from sessionStorage.
 *
 * @returns The saved partial values, or null when absent/unreadable.
 */
function readPersistedValues(): Partial<OnboardingValues> | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<OnboardingValues>;
    delete parsed.paymentUrl;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Persists onboarding form values (excluding ephemeral fields) to sessionStorage.
 *
 * @param values - The current onboarding form values.
 */
function persistValues(values: OnboardingValues): void {
  try {
    const { paymentUrl: _p, ...safe } = values;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(safe));
  } catch { /* quota exceeded or private mode */ }
}

/**
 * Clears all persisted onboarding state from sessionStorage.
 */
export function clearPersistedOnboarding(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem('onboarding:paymentUrl');
  } catch { /* noop */ }
}

/**
 * Forgets the payment attempt, and nothing else.
 *
 * Stepping back from the card form is not the same as finishing: the agency
 * has already been written by then -- it is created before the session is
 * opened -- and `agencyId` with `agencyPayerKey` are what let the next
 * attempt reuse that row instead of writing a second one.
 *
 * Wiping everything here is how one buyer ended up with two agencies of the
 * same name, one pending and one real, and no way to be rid of the first.
 * What has to go is the session: its URL outlives the step and a stale one
 * points the frame at a checkout that no longer exists.
 */
export function clearPersistedPaymentAttempt(): void {
  try {
    sessionStorage.removeItem('onboarding:paymentUrl');
  } catch { /* noop */ }
}

/**
 * Whether a saved value should be restored for a given key.
 *
 * @param key - The onboarding value key.
 * @param saved - The saved partial values.
 * @returns True when the value is present and not ephemeral.
 */
function isRestorable(key: keyof OnboardingValues, saved: Partial<OnboardingValues>): boolean {
  if (EPHEMERAL_KEYS.has(key)) return false;
  const val = saved[key];
  return val !== undefined && val !== null;
}

/**
 * Builds the restorable subset of saved values keyed by the initial schema.
 *
 * @param saved - The saved partial values.
 * @returns A record of values safe to merge back into the form.
 */
function buildMergedValues(saved: Partial<OnboardingValues>): Record<string, unknown> {
  const keys = Object.keys(onboardingInitialValues) as (keyof OnboardingValues)[];
  const merged: Record<string, unknown> = {};
  for (const key of keys) {
    if (isRestorable(key, saved)) merged[key] = saved[key];
  }
  return merged;
}

/**
 * Restores onboarding form values from sessionStorage on mount,
 * and persists them on every change so the wizard survives page reloads.
 */
export function usePersistedOnboarding(): void {
  const { useStore } = onboardingFormContext;
  const values = useStore((state) => state.values) as OnboardingValues;
  const updateValues = useStore((state) => state.updateValues);
  const restoredRef = useRef(false);

  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    const saved = readPersistedValues();
    if (!saved) return;
    const merged = buildMergedValues(saved);
    if (Object.keys(merged).length > 0) updateValues(merged);
  }, [updateValues]);

  useEffect(() => {
    if (!restoredRef.current) return;
    persistValues(values);
  }, [values]);
}
